import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { PrimeArtifact } from '../types/primeArtifact';

interface TreeStateContextType {
  expandedNodes: Set<string>;
  toggleNode: (nodeId: string) => void;
  expandNode: (nodeId: string) => void;
  collapseNode: (nodeId: string) => void;
  isNodeExpanded: (nodeId: string) => boolean;
  saveTreeState: () => void;
  restoreTreeState: () => void;
  expandNodeByName: (nodeName: string, artifacts: PrimeArtifact[]) => void;
  collapseAllNodes: () => void;
}

const TreeStateContext = createContext<TreeStateContextType | undefined>(undefined);

export const TreeStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [savedState, setSavedState] = useState<Set<string> | null>(null);
  const [expandedNodeNames, setExpandedNodeNames] = useState<Set<string>>(new Set());
  const [savedNodeNames, setSavedNodeNames] = useState<Set<string> | null>(null);
  const isRestoringState = useRef(false);

  // Removed verbose mount/unmount logs

  const toggleNode = useCallback((nodeId: string) => {
    console.log('[TreeStateContext] toggleNode called:', { nodeId });
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  const expandNode = useCallback((nodeId: string) => {
    console.log('[TreeStateContext] expandNode called:', { nodeId });
    setExpandedNodes((prev) => {
      // If the node is already expanded, don't update state
      if (prev.has(nodeId)) {
        return prev;
      }
      const newSet = new Set(prev);
      newSet.add(nodeId);
      return newSet;
    });
  }, []);

  const collapseNode = useCallback((nodeId: string) => {
    console.log('[TreeStateContext] collapseNode called:', { nodeId });
    setExpandedNodes((prev) => {
      // If the node is already collapsed, don't update state
      if (!prev.has(nodeId)) {
        return prev;
      }
      const newSet = new Set(prev);
      newSet.delete(nodeId);
      return newSet;
    });
  }, []);

  const collapseAllNodes = useCallback(() => {
    console.log('[TreeStateContext] collapseAllNodes called');
    setExpandedNodes(new Set());
    setExpandedNodeNames(new Set());
  }, []);

  const isNodeExpanded = useCallback(
    (nodeId: string) => {
      return expandedNodes.has(nodeId);
    },
    [expandedNodes]
  );

  const expandNodeByName = useCallback(
    (nodeName: string, artifacts: PrimeArtifact[]) => {
      if (isRestoringState.current) return;

      console.log('[TreeStateContext] expandNodeByName called:', { nodeName });

      // Find the artifact by name
      const findArtifactByName = (name: string, items: PrimeArtifact[]): PrimeArtifact | null => {
        for (const item of items) {
          if (item.name === name) {
            return item;
          }
          if (item.children && item.children.length > 0) {
            const found = findArtifactByName(name, item.children);
            if (found) return found;
          }
        }
        return null;
      };

      const artifact = findArtifactByName(nodeName, artifacts);
      if (artifact) {
        console.log('[TreeStateContext] Found artifact by name, expanding:', {
          name: artifact.name,
          id: artifact.id,
        });
        expandNode(artifact.id);
        setExpandedNodeNames((prev) => {
          // If the node name is already in the set, don't update state
          if (prev.has(nodeName)) {
            return prev;
          }
          const newSet = new Set(prev);
          newSet.add(nodeName);
          return newSet;
        });
      } else {
        console.log('[TreeStateContext] Artifact not found by name:', nodeName);
      }
    },
    [expandNode]
  );

  const saveTreeState = useCallback(() => {
    console.log('[TreeStateContext] saveTreeState called');
    setSavedState(new Set(expandedNodes));
    setSavedNodeNames(new Set(expandedNodeNames));
  }, [expandedNodes, expandedNodeNames]);

  const restoreTreeState = useCallback(() => {
    if (isRestoringState.current) return;

    console.log('[TreeStateContext] restoreTreeState called');
    isRestoringState.current = true;

    try {
      if (savedState) {
        setExpandedNodes(new Set(savedState));
      }
      if (savedNodeNames) {
        setExpandedNodeNames(new Set(savedNodeNames));
      }
    } finally {
      isRestoringState.current = false;
    }
  }, [savedState, savedNodeNames]);

  const value = useMemo(
    () => ({
      expandedNodes,
      toggleNode,
      expandNode,
      collapseNode,
      isNodeExpanded,
      saveTreeState,
      restoreTreeState,
      expandNodeByName,
      collapseAllNodes,
    }),
    [
      expandedNodes,
      toggleNode,
      expandNode,
      collapseNode,
      isNodeExpanded,
      saveTreeState,
      restoreTreeState,
      expandNodeByName,
      collapseAllNodes,
    ]
  );

  return <TreeStateContext.Provider value={value}>{children}</TreeStateContext.Provider>;
};

export const useTreeState = () => {
  const context = useContext(TreeStateContext);
  if (context === undefined) {
    throw new Error('useTreeState must be used within a TreeStateProvider');
  }
  return context;
};
